import React, { useState, useCallback } from 'react';
import { PenLine, PlusCircle, Trash2, Edit2, Loader2 } from 'lucide-react';
import { useEmailSignatures } from '@/hooks/email/useEmailSignatures';
import { EmailSignature, EmailSignatureInput } from '@/services/api/emailSignatures';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { stripHtml, textToHtml } from '@/utils/emailHtml';
import RichEmailEditor from '@/components/email/editor/RichEmailEditor';

interface EmailSignaturesPanelProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

const defaultForm = (): EmailSignatureInput => ({
  name: '',
  body_html: '',
  is_default: false,
});

/**
 * Signatures CRUD panel — lives on the Email Templates page as a tab.
 * The composer appends signature body_html client-side; nothing is
 * auto-appended server-side.
 */
const EmailSignaturesPanel: React.FC<EmailSignaturesPanelProps> = ({
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) => {
  const {
    signatures,
    isLoading,
    refetch,
    createSignature,
    updateSignature,
    deleteSignature,
    isCreating,
    isUpdating,
    isDeleting,
  } = useEmailSignatures();

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selected, setSelected] = useState<EmailSignature | null>(null);
  const [form, setForm] = useState<EmailSignatureInput>(defaultForm());
  const [deleteTarget, setDeleteTarget] = useState<EmailSignature | null>(null);

  const handleCreateNew = useCallback(() => {
    setSelected(null);
    setForm(defaultForm());
    setIsEditorOpen(true);
  }, []);

  const handleEdit = useCallback((signature: EmailSignature) => {
    setSelected(signature);
    setForm({
      name: signature.name,
      body_html: signature.body_html || signature.body_text || '',
      is_default: signature.is_default === true,
    });
    setIsEditorOpen(true);
  }, []);

  const handleSave = async () => {
    if (!form.name.trim() || !(form.body_html || '').trim()) return;
    const raw = (form.body_html || '').trim();
    // Accept either HTML or plain text; keep both fields populated.
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(raw);
    const body_html = looksLikeHtml ? raw : textToHtml(raw);
    const payload: EmailSignatureInput = {
      name: form.name.trim(),
      body_html,
      body_text: stripHtml(body_html),
      is_default: form.is_default === true,
    };
    if (selected) {
      await updateSignature({ id: selected.id, data: payload });
    } else {
      await createSignature(payload);
    }
    setIsEditorOpen(false);
    setSelected(null);
    setForm(defaultForm());
    refetch();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteSignature(deleteTarget.id);
    setDeleteTarget(null);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Signatures are appended below your message when composing. One default is pre-selected on new emails.
        </p>
        {canCreate && (
          <Button onClick={handleCreateNew}>
            <PlusCircle className="mr-2 h-4 w-4" />
            New Signature
          </Button>
        )}
      </div>

      {signatures.length === 0 ? (
        <div className="border-2 border-dashed border-muted rounded-lg p-12 text-center">
          <PenLine className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No signatures yet</h3>
          <p className="text-muted-foreground mb-6">
            Create a signature to append it to your emails with one click.
          </p>
          {canCreate && (
            <Button onClick={handleCreateNew}>
              <PlusCircle className="mr-2 h-4 w-4" />
              New Signature
            </Button>
          )}
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Preview</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signatures.map((signature) => (
                <TableRow key={signature.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{signature.name}</span>
                      {signature.is_default && (
                        <Badge variant="secondary">Default</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <p className="truncate text-sm text-muted-foreground">
                      {stripHtml(signature.body_html || '') || signature.body_text || '—'}
                    </p>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {canDelete && (
                        <Button variant="outline" size="sm" onClick={() => setDeleteTarget(signature)}>
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      )}
                      {canUpdate && (
                        <Button variant="default" size="sm" onClick={() => handleEdit(signature)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected ? 'Edit Signature' : 'Create Signature'}</DialogTitle>
            <DialogDescription>
              Plain text or HTML. This is appended below your message when you pick it in the composer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="signature-name">Signature Name</Label>
              <Input
                id="signature-name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Sales"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signature-body">Content</Label>
              <RichEmailEditor
                value={form.body_html || ''}
                onChange={(html) => setForm((prev) => ({ ...prev, body_html: html }))}
                minHeight="180px"
                maxHeight="40vh"
                placeholder="— Jane Doe, Head of Sales, Acme Inc."
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="signature-default"
                checked={form.is_default === true}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_default: checked }))}
              />
              <Label htmlFor="signature-default" className="cursor-pointer">
                Default signature
              </Label>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Setting this as default un-defaults your other signatures.
            </p>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setIsEditorOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isCreating || isUpdating || !form.name.trim() || !(form.body_html || '').trim()}
              >
                {(isCreating || isUpdating) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Signature
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Signature?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteTarget?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailSignaturesPanel;
